"use client";

import { queryOptions, useQuery } from "@tanstack/react-query";

import { getSellableMembershipPlan } from "@/consts/membership";
import type { PermissionId } from "@/lib/permissions/catalog";
import type { UserRole } from "@/lib/permissions/roles";
import type { MembershipPlanId, PlanFeatureId, User, UserMembership } from "@/types/membership";

export const userPermissionsQueryKey = (userId: string) => ["userPermissions", userId] as const;

export const userMembershipQueryKey = (userId: string) => ["userMembership", userId] as const;

export const userSecurityFlagsQueryKey = (userId: string) =>
	["user", "securityFlags", userId] as const;

export type UserPermissionsPayload = {
	role: UserRole;
	permissions: PermissionId[] | readonly ["*"];
	grantsRevision?: number;
};

export interface MembershipApiResponse {
	planId: MembershipPlanId;
	effectiveTier?: "free" | "pro";
	planProfileId?: string | null;
	isActive: boolean;
	endDate?: string | null;
	billingPeriod?: "monthly" | "yearly";
	featureIds?: PlanFeatureId[];
	hasStripeSubscription?: boolean;
	hasStripeCustomerId?: boolean;
	accessSource?: UserMembership["accessSource"];
	partnerAccessEndDate?: string | null;
	daysUntilAccessEnds?: number | null;
	billingResumesAt?: string | null;
	partnerName?: string | null;
	partnerBundleExpired?: boolean;
	accessScenario?: string;
	accessBadgeLabel?: string;
	accessSummary?: string;
	showBillingPortal?: boolean;
	showCancelSubscription?: boolean;
	showCheckoutCta?: boolean;
}

export type UserSecurityFlagsPayload = {
	mustChangePassword: boolean;
	initialPasswordPending: boolean;
};

export async function fetchUserPermissions(): Promise<UserPermissionsPayload | null> {
	const response = await fetch("/api/user/permissions", { credentials: "include" });
	if (!response.ok) {
		return null;
	}
	return response.json() as Promise<UserPermissionsPayload>;
}

export async function fetchUserMembership(): Promise<MembershipApiResponse | null> {
	const response = await fetch("/api/user/membership", { credentials: "include" });
	if (response.status === 401) return null;
	if (!response.ok) return null;
	return response.json() as Promise<MembershipApiResponse>;
}

export async function fetchUserSecurityFlags(): Promise<UserSecurityFlagsPayload> {
	const response = await fetch("/api/user/security-flags", { credentials: "include" });
	if (!response.ok) {
		return { mustChangePassword: false, initialPasswordPending: false };
	}
	return response.json() as Promise<UserSecurityFlagsPayload>;
}

export function userPermissionsQueryOptions(userId: string) {
	return queryOptions({
		queryKey: userPermissionsQueryKey(userId),
		queryFn: fetchUserPermissions,
		staleTime: 5 * 60 * 1000,
		gcTime: 10 * 60 * 1000,
		retry: 1,
		refetchOnWindowFocus: true,
		refetchInterval: (query) => (query.state.data?.role === "support" ? 30_000 : false),
	});
}

export function userMembershipQueryOptions(userId: string) {
	return queryOptions({
		queryKey: userMembershipQueryKey(userId),
		queryFn: fetchUserMembership,
		staleTime: 60 * 1000,
		gcTime: 5 * 60 * 1000,
		retry: 1,
	});
}

export function userSecurityFlagsQueryOptions(userId: string) {
	return queryOptions({
		queryKey: userSecurityFlagsQueryKey(userId),
		queryFn: fetchUserSecurityFlags,
		staleTime: 30 * 1000,
		gcTime: 5 * 60 * 1000,
		retry: 1,
	});
}

export function useUserPermissions(userId: string | null | undefined) {
	return useQuery({
		...userPermissionsQueryOptions(userId ?? "_"),
		enabled: !!userId,
	});
}

export function useUserMembership(userId: string | null | undefined) {
	return useQuery({
		...userMembershipQueryOptions(userId ?? "_"),
		enabled: !!userId,
	});
}

export function useUserSecurityFlags(userId: string | null | undefined) {
	return useQuery({
		...userSecurityFlagsQueryOptions(userId ?? "_"),
		enabled: !!userId,
	});
}

export function mergeMembershipFromApi(
	debugPlan: MembershipPlanId | null,
	api: MembershipApiResponse | null | undefined,
	createdAt: Date,
): UserMembership {
	if (debugPlan) {
		const def = getSellableMembershipPlan(debugPlan);
		return {
			planId: debugPlan,
			startDate: createdAt,
			isActive: true,
			billingPeriod: "monthly",
			featureIds: def?.features,
		};
	}

	if (!api) {
		return {
			planId: "free",
			startDate: createdAt,
			isActive: true,
			billingPeriod: "monthly",
		};
	}

	return {
		planId: api.planId,
		planProfileId: api.planProfileId,
		featureIds: api.featureIds,
		hasStripeSubscription: api.hasStripeSubscription,
		hasStripeCustomerId: api.hasStripeCustomerId,
		startDate: createdAt,
		endDate: api.endDate ? new Date(api.endDate) : undefined,
		isActive: api.isActive,
		billingPeriod: api.billingPeriod ?? "monthly",
		accessSource: api.accessSource,
		partnerAccessEndDate: api.partnerAccessEndDate ?? null,
		daysUntilAccessEnds: api.daysUntilAccessEnds ?? null,
		billingResumesAt: api.billingResumesAt ?? null,
		partnerName: api.partnerName ?? null,
		partnerBundleExpired: api.partnerBundleExpired ?? false,
		accessScenario: api.accessScenario,
		accessBadgeLabel: api.accessBadgeLabel,
		accessSummary: api.accessSummary,
		showBillingPortal: api.showBillingPortal,
		showCancelSubscription: api.showCancelSubscription,
		showCheckoutCta: api.showCheckoutCta,
	};
}

/** Map better-auth session user + query payloads into our `User` type. */
export function mapBetterAuthUserToUser(
	betterAuthUser: {
		id: string;
		email: string;
		name?: string | null;
		firstName?: string | null;
		lastName?: string | null;
		createdAt?: Date | string;
		updatedAt?: Date | string;
	},
	role: UserRole | null | undefined,
	permissions: PermissionId[] | readonly ["*"] | undefined,
	membership: UserMembership,
	securityFlags?: UserSecurityFlagsPayload | null,
): User {
	const firstName =
		betterAuthUser.firstName ||
		betterAuthUser.name?.split(" ")[0] ||
		betterAuthUser.email?.split("@")[0] ||
		"User";
	const lastName =
		betterAuthUser.lastName || betterAuthUser.name?.split(" ").slice(1).join(" ") || "";

	const createdAt = new Date(betterAuthUser.createdAt || Date.now());

	return {
		id: betterAuthUser.id,
		email: betterAuthUser.email,
		firstName,
		lastName,
		role: role || undefined,
		permissions: permissions?.length ? permissions : undefined,
		membership,
		mustChangePassword: securityFlags?.mustChangePassword ?? false,
		initialPasswordPending: securityFlags?.initialPasswordPending ?? false,
		createdAt,
		updatedAt: new Date(betterAuthUser.updatedAt || Date.now()),
	};
}
